/* eslint-disable @typescript-eslint/no-empty-function */
import { computed, inject, Injectable, signal } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
    delay,
    from,
    Observable,
    OperatorFunction,
    retryWhen,
    scan,
    Subscription,
    take,
    tap,
    throwError,
} from 'rxjs';
import { LoggerService } from '@dxp/core/services';
import { ISignalRStatistics } from '@dxp/core/models';

export const RETRY_LIMIT = 3;
export const RETRY_INTERVAL_MS = 1000;

@Injectable({
    providedIn: 'root',
})
export abstract class SignalRHubService {
    readonly #oidcSecurityService = inject(OidcSecurityService);
    protected readonly logger = inject(LoggerService);

    protected hubConnection!: HubConnection;

    protected abstract name: string;
    protected abstract url: string;

    readonly #sendSubscriptions = new Set<Subscription>();
    readonly #lastMessageSignal = signal<Date | undefined>(undefined);
    readonly #messageCountSignal = signal<number>(0);
    readonly #connectionStateSignal = signal<HubConnectionState>(
        HubConnectionState.Disconnected,
    );

    statistics = computed<ISignalRStatistics>(() => {
        return {
            state: this.#connectionStateSignal(),
            lastMessage: this.#lastMessageSignal(),
            count: this.#messageCountSignal(),
        };
    });

    start(parameters?: Record<string, string[]>) {
        this.#oidcSecurityService
            .getAccessToken()
            .pipe(take(1))
            .subscribe(token => {
                this.hubConnection = new HubConnectionBuilder()
                    .withUrl(
                        `${this.url}${this.#parseParameters(parameters)}`,
                        {
                            accessTokenFactory: () => token,
                        },
                    )
                    .configureLogging(LogLevel.Error)
                    .withAutomaticReconnect()
                    .build();

                this.hubConnection.onreconnected(() => {
                    this.logger.info(
                        `${this.name} Service -> Reconnected successfully`,
                    );

                    this.#updateConnectionState(HubConnectionState.Connected);
                });

                this.hubConnection.onclose(() => {
                    this.logger.info(
                        `${this.name} Service -> Connection closed`,
                    );

                    this.#updateConnectionState(
                        HubConnectionState.Disconnected,
                    );
                });

                this.hubConnection
                    .start()
                    .then(() => {
                        this.#updateConnectionState(
                            HubConnectionState.Connected,
                        );
                        this.registerHandlers();
                        this.onStart();

                        this.logger.info(
                            `${this.name} Service -> Connection started`,
                        );
                    })
                    .catch(error =>
                        this.logger.error(
                            `${this.name} Service -> Error while starting connection`,
                            error,
                        ),
                    );
            });
    }

    stop(): Promise<void> {
        this.#sendSubscriptions.forEach(subscription =>
            subscription.unsubscribe(),
        );
        this.#sendSubscriptions.clear();

        if (this.hubConnection) {
            this.unregisterHandlers();

            return this.hubConnection
                .stop()
                .then(() => {
                    this.#updateConnectionState(
                        HubConnectionState.Disconnected,
                    );
                    this.onStop();

                    this.logger.info(
                        `${this.name} Service -> Connection stopped`,
                    );
                })
                .catch(error =>
                    this.logger.error(
                        `${this.name} Service -> Error while stopping connection`,
                        error,
                    ),
                );
        }

        return Promise.resolve();
    }

    restart(parameters?: Record<string, string[]>) {
        this.stop()
            .then(() => {
                this.logger.info(
                    `${this.name} Service -> Restarting connection`,
                );
                this.start(parameters);
            })
            .catch(error => {
                this.logger.error(
                    `${this.name} Service -> Error while restarting connection`,
                    error,
                );
            });
    }

    send(command: string, ...args: any[]) {
        if (this.state !== HubConnectionState.Connected) {
            this.logger.error(
                `${this.name} Service -> Cannot send (${command}): Not connected`,
            );
            return;
        }
        const sendAttempt = () =>
            from(this.hubConnection.send(command, ...args)).pipe(
                tap(() =>
                    this.logger.debug(
                        `${this.name} Service -> Command (${command}) sent successfully`,
                    ),
                ),
                this.#withRetry<void>(command),
            );

        const subscription = sendAttempt().subscribe({
            error: error =>
                this.logger.error(
                    `${this.name} Service -> Failed to send command (${command}) after retries.`,
                    error,
                ),
        });

        this.#sendSubscriptions.add(subscription);
        subscription.add(() => this.#sendSubscriptions.delete(subscription));
    }

    invoke<T>(command: string, ...args: any[]): Observable<T> {
        if (this.state !== HubConnectionState.Connected) {
            this.logger.error(
                `${this.name} Service -> Cannot invoke (${command}): Not connected`,
            );
            return throwError(
                () => new Error(`${this.name} Service -> Not connected`),
            );
        }

        return from(this.hubConnection.invoke(command, ...args)).pipe(
            tap(() =>
                this.logger.info(
                    `${this.name} Service -> Command (${command}) invoked successfully`,
                ),
            ),
            this.#withRetry<T>(command),
        );
    }

    get state(): HubConnectionState {
        return this.hubConnection
            ? this.hubConnection.state
            : HubConnectionState.Disconnected;
    }

    protected messageReceived() {
        this.#lastMessageSignal.set(new Date());
        this.#messageCountSignal.update(count => count + 1);
    }

    #parseParameters(parameters?: Record<string, string[]>): string {
        if (parameters) {
            return `?${Object.keys(parameters)
                .map(key => `${key}=${parameters[key].join(',')}`)
                .join('&')}`;
        }

        return '';
    }

    #updateConnectionState(state: HubConnectionState) {
        this.#connectionStateSignal.set(state);
    }

    #withRetry = <T>(command: string): OperatorFunction<T, T> => {
        return retryWhen(errors =>
            errors.pipe(
                scan((retryCount, err) => {
                    if (retryCount >= RETRY_LIMIT) {
                        throw err;
                    }
                    this.logger.warn(
                        `${this.name} Service -> Retry ${retryCount + 1} for command (${command})`,
                    );
                    return retryCount + 1;
                }, 0),
                delay(RETRY_INTERVAL_MS),
            ),
        );
    };

    protected onStart() {}
    protected onStop() {}
    protected registerHandlers() {}
    protected unregisterHandlers() {}
}
